import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, Eye, Trash2, Plus } from "lucide-react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string; // 'active' or 'non-active'
  password?: string;
  created_at: string;
}

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewUser, setViewUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user', status: 'active' });
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }

  async function handleAddUser(e: React.FormEvent) {
    e.preventDefault();
    setAddLoading(true);
    try {
      // First create the user with auth.signUp
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUser.email,
        password: newUser.password,
        options: {
          data: {
            name: newUser.name,
            role: newUser.role,
            status: newUser.status
          }
        }
      });
      
      if (authError) throw authError;
      
      // Update the profile with additional information if needed
      if (authData.user) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            name: newUser.name,
            role: newUser.role,
            status: newUser.status
          })
          .eq('id', authData.user.id);
          
        if (profileError) throw profileError;
      }
      
      setAddDialogOpen(false);
      setNewUser({ name: '', email: '', password: '', role: 'user', status: 'active' });
      fetchUsers();
    } catch (error) {
      alert('Failed to add user: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setAddLoading(false);
    }
  }

  async function handleDeleteUser() {
    if (!deleteUser) return;
    try {
      await supabase.from('profiles').delete().eq('id', deleteUser.id);
      setDeleteUser(null);
      fetchUsers();
    } catch (error) {
      alert('Failed to delete user: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-6"><p className="text-red-500">Error: {error}</p></Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground mt-1">Manage and view all users in the system.</p>
        </div>
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="default" size="sm" className="gap-2"><Plus className="w-4 h-4" /> Add New</Button>
          </DialogTrigger>
          <DialogContent aria-describedby="add-user-desc">
  <span id="add-user-desc" className="sr-only">Fill out the form to add a new user.</span>
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium">Name</label>
                <input type="text" className="input input-bordered w-full" required value={newUser.name} onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))} />
              </div>
              <div>
                <label className="block mb-1 font-medium">Email</label>
                <input type="email" className="input input-bordered w-full" required value={newUser.email} onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} />
              </div>
              <div>
                <label className="block mb-1 font-medium">Password</label>
                <input type="password" className="input input-bordered w-full" required value={newUser.password} onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} />
              </div>
              <div>
                <label className="block mb-1 font-medium">Role</label>
                <select className="input input-bordered w-full" value={newUser.role} onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="vendor">Vendor</option>
                </select>
              </div>
              <div>
                <label className="block mb-1 font-medium">Status</label>
                <select className="input input-bordered w-full" value={newUser.status} onChange={e => setNewUser(u => ({ ...u, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="non-active">Non-active</option>
                </select>
              </div>

              <DialogFooter>
                <Button type="submit" disabled={addLoading}>{addLoading ? 'Adding...' : 'Add User'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>
      <div className="overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.id}>
                <TableCell className="flex items-center gap-2 py-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user.avatar_url || undefined} alt={user.name} />
                    <AvatarFallback>{user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium">{user.name}</span>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Badge variant={user.role === 'admin' ? 'destructive' : 'secondary'}>{user.role}</Badge>
                </TableCell>
                <TableCell>
                  <select
                    className="input input-bordered input-xs w-auto px-2 py-1 rounded"
                    value={user.status}
                    onChange={async (e) => {
                      const newStatus = e.target.value;
                      await supabase.from('profiles').update({ status: newStatus }).eq('id', user.id);
                      fetchUsers();
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="non-active">Non-active</option>
                  </select>
                  <Badge className={user.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}>
                    {user.status === 'active' ? 'Active' : 'Non-active'}
                  </Badge>
                </TableCell>
                <TableCell>{new Date(user.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="icon" onClick={() => setViewUser(user)}><Eye className="w-4 h-4" /></Button>
                      </DialogTrigger>
                      <DialogContent aria-describedby="view-user-desc">
  <span id="add-user-desc" className="sr-only">Fill out the form to add a new user.</span>
                        <DialogHeader>
                          <DialogTitle>User Info</DialogTitle>
                        </DialogHeader>
                        <div className="flex flex-col items-center gap-2">
                          <Avatar className="h-16 w-16">
                            <AvatarImage src={user.avatar_url || undefined} alt={user.name} />
                            <AvatarFallback>{user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U'}</AvatarFallback>
                          </Avatar>
                          <div className="text-lg font-bold">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          <Badge variant={user.role === 'admin' ? 'destructive' : 'secondary'}>{user.role}</Badge>
                          <Badge className={user.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}>
                            {user.status === 'active' ? 'Active' : 'Non-active'}
                          </Badge>
                          <div className="text-xs text-muted-foreground">Joined {new Date(user.created_at).toLocaleDateString()}</div>
                        </div>
                        <DialogFooter>
                          <DialogClose asChild>
                            <Button variant="outline">Close</Button>
                          </DialogClose>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                    <Dialog open={deleteUser?.id === user.id} onOpenChange={open => open ? setDeleteUser(user) : setDeleteUser(null)}>
                      <DialogTrigger asChild>
                        <Button variant="destructive" size="icon" onClick={() => setDeleteUser(user)}><Trash2 className="w-4 h-4" /></Button>
                      </DialogTrigger>
                      <DialogContent aria-describedby="add-user-desc">
  <span id="add-user-desc" className="sr-only">Fill out the form to add a new user.</span>
                        <DialogHeader>
                          <DialogTitle>Delete User</DialogTitle>
                        </DialogHeader>
                        <p>Are you sure you want to delete <span className="font-semibold">{user.name}</span>?</p>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setDeleteUser(null)}>Cancel</Button>
                          <Button variant="destructive" onClick={handleDeleteUser}>Delete</Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
